import { JobStatus } from '@bp/bp-graphql-types';
import { Button, DeleteIcon, Table, TableColumns } from '@bp/ui-components';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

export const EventJobsTableFragment = graphql(`
  fragment EventJobsTableFields on Query {
    eventJobs(where: $eventJobWhere, options: $eventJobOptions) {
      __typename
      uuid
      createdAt
      from
      to
      eventsToProcess
      processed
      status
    }
  }
`);

type PlanungImportEventsJobTableType = {
  uuid: string;
  created: string;
  from: string;
  until: string;
  processed: number;
  eventsToProcess: number;
  status: JobStatus;
};

type PlanungImportEventsJobTableProps = {
  query: FragmentType<typeof EventJobsTableFragment> | undefined;
  dangerHandleCancelClick: (jobUuid: string) => void;
};

export const PlanungImportEventsJobTable = ({ query, dangerHandleCancelClick }: PlanungImportEventsJobTableProps) => {
  const { t } = useTranslation();

  const fragment = getFragmentData(EventJobsTableFragment, query);
  const eventJobs = fragment?.eventJobs ?? [];

  const columns: TableColumns<PlanungImportEventsJobTableType>[] = [
    {
      id: 'created',
      accessorKey: 'created',
      header: t('common.createdAt'),
    },
    {
      id: 'from',
      accessorKey: 'from',
      header: t('planungImport.from'),
    },
    {
      id: 'until',
      accessorKey: 'until',
      header: t('planungImport.until'),
    },
    {
      id: 'processed',
      accessorKey: 'processed',
      header: t('common.processed'),
      accessorFn: (row) => row.processed + '/' + row.eventsToProcess,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: t('common.status'),
      accessorFn: ({ status }) => getStatusTranslation(status),
      size: 250,
    },
  ];

  const data: PlanungImportEventsJobTableType[] = eventJobs.map((job) => {
    const createdAt = dayjs(job.createdAt);
    const from = dayjs(job.from);
    const to = dayjs(job.to);
    return {
      uuid: job.uuid,
      created: createdAt.format('DD.MM.YYYY, HH:mm'),
      from: from.isSame(createdAt, 'day') ? from.format('HH:mm') : from.format('DD.MM.YYYY'),
      until: to.isSame(createdAt, 'day') ? to.format('HH:mm') : to.format('DD.MM.YYYY'),
      processed: job.processed ?? 0,
      eventsToProcess: job.eventsToProcess ?? 0,
      status: job.status,
    };
  });

  function getStatusTranslation(status: JobStatus): string {
    switch (status) {
      case JobStatus.Cancelled:
        return t('planungImport.status.cancelled');
      case JobStatus.Error:
        return t('planungImport.status.error');
      case JobStatus.Finished:
        return t('planungImport.status.finished');
      case JobStatus.Running:
        return t('planungImport.status.running');
      default:
        return t('planungImport.status.error');
    }
  }

  return (
    <Table
      data={data}
      columns={columns}
      hideHeader={data.length === 0}
      customPadding='var(--spacing-7)'
      emptyStateSettings={{ title: '', subtitle: '' }}
      lastCol={(row) => {
        if (row.original.status === JobStatus.Running) {
          return (
            <Button
              hierarchy={'secondary'}
              icon={<DeleteIcon />}
              onClick={() => dangerHandleCancelClick(row.original.uuid)}
            />
          );
        }
      }}
      // lastColWidth='0'
      showPagination
      pageSize={20}
    />
  );
};
